import { Suspense } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { fetchCampaignCustomFields } from 'api/campaigns';
import * as CampaignToolsAddCustomField from 'assets/js/pages/campaign-settings/campaign-tools/CampaignToolsAddCustomField';
import { CampaignToolsViewCustomField } from 'assets/js/pages/campaign-settings/campaign-tools/CampaignToolsViewCustomField';
import * as DropdownMenu from 'components/dropdown-menu';
import { Permissions } from 'components/permissions';
import { CampaignPermission } from 'components/permissions/PermissionEnums';
import * as SettingsCard from 'components/settings-card';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { capitalize } from 'lodash';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CustomField } from 'types/domain';
import { CustomFieldType } from 'types/filter';

const DROPDOWN_TYPES: CustomFieldType[] = [
    'text',
    'textarea',
    'checkbox',
    'select',
    'multiselect',
    'radio',
    'file',
];

interface CustomFieldSettingsProps {
    campaignId: number;
}

const CampaignToolsCustomFields = ({ campaignId }: CustomFieldSettingsProps) => {
    const { hasGivebutterPlusAccess } = useCurrentAccount();
    const { data: customFields = [] } = useSuspenseQuery({
        queryKey: campaignQueryKeys.customFields(campaignId),
        queryFn: () => fetchCampaignCustomFields(campaignId),
    });

    const availableDropdownTypes = DROPDOWN_TYPES.filter(
        type => type !== 'file' || hasGivebutterPlusAccess,
    );

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>Custom fields</SettingsCard.Heading>
                <p className={'text-sm text-gray-700'}>Manage your campaign's custom fields.</p>
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={
                        'https://help.givebutter.com/en/articles/3586683-how-to-add-custom-fields-to-a-campaign'
                    }
                    className={'text-sm'}
                >
                    Read the guide
                </a>
                .
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide className="flex flex-col items-start gap-4">
                <Permissions
                    allowed={[CampaignPermission.EDIT]}
                    fallback={
                        customFields.length == 0 ? (
                            <p className="text-sm text-gray-700">
                                No custom fields have been created yet.
                            </p>
                        ) : null
                    }
                >
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild={true}>
                            <Button variant="solid">New Custom Field</Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content sideOffset={4} align={'start'}>
                            {availableDropdownTypes.map(type => (
                                <CampaignToolsAddCustomField.Root
                                    key={type}
                                    campaignId={campaignId}
                                    type={type}
                                >
                                    <CampaignToolsAddCustomField.Trigger asChild={true}>
                                        <DropdownMenu.Item
                                            key={type}
                                            onSelect={e => e.preventDefault()}
                                        >
                                            <div className="flex gap-4 px-3 py-3 text-sm">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-gray-200">
                                                    <img
                                                        alt={`${capitalize(type)} Icon`}
                                                        src={`/images/icons/custom-fields/${type}.svg`}
                                                        className="h-4"
                                                    />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-black">
                                                        {capitalize(type)}
                                                    </p>
                                                    <p className="text-black">
                                                        {getFieldTypeDescription(type)}
                                                    </p>
                                                </div>
                                            </div>
                                        </DropdownMenu.Item>
                                    </CampaignToolsAddCustomField.Trigger>
                                </CampaignToolsAddCustomField.Root>
                            ))}
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                </Permissions>
                {customFields.map(field => (
                    <CampaignToolsViewCustomField
                        field={field}
                        key={field.id}
                        fieldOrder={customFields.map((customField: CustomField) => customField.id)}
                        campaignId={campaignId}
                    />
                ))}
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

const getFieldTypeDescription = (fieldType: CustomFieldType) => {
    switch (fieldType) {
        case 'checkbox':
            return 'Yes/no checkbox';
        case 'text':
            return 'Simple text field for collecting short answers';
        case 'textarea':
            return 'Larger text box for collecting long answers';
        case 'select':
            return 'Select a single option from a dropdown list of options';
        case 'multiselect':
            return 'Select multiple options from a dropdown list of options';
        case 'radio':
            return 'Select a single option from a list of options';
        case 'file':
            return 'Upload a file';
        default:
            return `Description of the ${fieldType} field`;
    }
};

const WrappedCampaignToolsCustomFields = () => {
    const { data: campaign } = useCurrentCampaign();

    if (!campaign) {
        return null;
    }

    return (
        <Suspense
            fallback={
                <div className={'m-auto'}>
                    <Loader variant="static" />
                </div>
            }
        >
            <CampaignToolsCustomFields campaignId={campaign.id} />
        </Suspense>
    );
};

export { WrappedCampaignToolsCustomFields as CampaignToolsCustomFields };
